import ModalViewModel from '../../../infra/ModalViewModel'
import { task } from 'mobx-task'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { Id } from '../../../types/definitions'
import MemberStore from '../../../iam/MemberStore'
import Workspace from '../../../iam/models/Workspace'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import { SearchableUser } from '../../../iam/models/SearchableUser'
import { func, required, validationContext } from 'validx'

export class RemoveFromWorkspaceViewModel extends ModalViewModel {
  @observable
  private _selectedUserIds: Set<Id> = new Set()

  @observable
  selectedWorkspace?: Workspace = undefined

  @observable
  selectedUser?: SearchableUser = undefined

  @observable
  workspace: Workspace = Workspace.none

  @observable
  confirmation: string = ''

  validation = validationContext(this, {
    confirmation: [
      required({ msg: 'This field is required' }),
      func(
        ({ value }) =>
          value?.toLowerCase() === 'remove' || 'You must type "remove"',
      ),
    ],
  })

  constructor(
    private memberStore: MemberStore,
    private newAlert: AlertFactory,
    private removeUsers: (userIds: Array<Id>) => void,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @action
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  @action
  setSelectedUser(user: SearchableUser) {
    this.selectedUser = user
    this.setSelectedUserIds(new Set([user.publicId]))
  }

  @action
  setSelectedUserIds(selectedUserIds: Set<Id>) {
    this._selectedUserIds = selectedUserIds
  }

  @action
  setSelectedWorkspace(workspace: Workspace) {
    this.selectedWorkspace = workspace
  }

  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this.setSelectedUserIds(selectedUserIds)
    this.setWorkspace(workspace)

    this.show()
  }

  @action.bound
  showWithUser(user: SearchableUser, workspace: Workspace) {
    this.setSelectedUser(user)
    this.setWorkspace(workspace)
    super.show()
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const alert = this.newAlert()

    alert
      .withState(AlertState.Pending)
      .saying('Removing User!')
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userId) =>
          this.memberStore.update(
            {
              user_public_id: Id.assert(userId),
              workspace_id: Id.assert(this.workspace.id),
            },
            { inactive: true },
          ),
        ),
      )

      this.removeUsers(this.selectedUserIds)

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'User removed successfully!')
        .autoDismiss()
        .show()

      this.close()
    } catch (e) {
      console.error(e)

      alert.fromError(e).autoDismiss()
    }
  })

  @action
  clear() {
    this.selectedWorkspace = undefined
    this.selectedUser = undefined
    this.workspace = Workspace.none
    this.validation.reset()
    this.confirmation = ''
  }

  @override
  close() {
    this.clear()
    super.close()
  }
}
